import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Switch, Checkbox, Select, Row, Col, Badge, Typography, Card, Divider } from 'antd';

import { InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

import * as recurrence from '../../../constants/recurrence-constants';

import moment from 'moment';
import _ from 'lodash';

import './edit-event-modal.less';
import FrequencyContainer from '../edit-event-form-elements/form-containers/frequency-container';
import EndsContainer from '../edit-event-form-elements/form-containers/ends-container';
import FrequencyItem from '../edit-event-form-elements/form-items/frequency-item';

const EditEventModal = ({ rootContainer, isOpen, title, eventToEdit, calendarCategories, saveEventLoading, handleModalOk, handleModalCancel }) => {
  const [form] = Form.useForm();

  const [selectedFrequency, setSelectedFrequency] = useState(recurrence.FREQUENCY_TYPES.Daily);
  const [ends, setEnds] = useState(recurrence.RECURRENCE_ENDS.NEVER);
  const [durationFormat, setDurationFormat] = useState('YYYY-MM-DD HH:mm');
  const [showDurationTime, setShowDurationTime] = useState('HH:mm');

  useEffect(() => {
    if (isOpen && eventToEdit) {
      const overriddenRules = initRules(eventToEdit);

      const defaultValues = {
        subject: eventToEdit.subject,
        duration: [eventToEdit.startObj, eventToEdit.endObj],
        type: +eventToEdit.type >= 0 ? eventToEdit.type.toString() : null,
        calendarCategoryId: eventToEdit.calendarCategoryId,
        reminderToCustomEmail: eventToEdit.reminderToCustomEmail,
        isCheckedReminder: eventToEdit.isCheckedReminder,
        agencyEMail: eventToEdit.agencyEMail,
        agentEMail: eventToEdit.agentEMail,
        csrEMail: eventToEdit.csrEMail,
        noneSending: eventToEdit.noneSending,
        isCheckedRecurrent: eventToEdit.isCheckedRecurrent,
        rules: overriddenRules,
      };

      form.setFieldsValue(defaultValues);
    }
  }, [isOpen, eventToEdit]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const isAllDayEvent = values.allDay;
        const [startEventDate, endEventDate] = [values.duration[0].utc(true), values.duration[1].utc(true)];

        //need for updating child recurring event
        const defaultRecurringDates =
          eventToEdit && eventToEdit.recurrenceObject
            ? {
                seriesStart: eventToEdit.recurrenceObject.seriesStart,
                seriesEnd: eventToEdit.recurrenceObject.seriesEnd,
                lastOccurenceDate: eventToEdit.recurrenceObject.lastOccurenceDate,
                excludeOccurencesPeriods: eventToEdit.recurrenceObject.excludeOccurencesPeriods,
              }
            : null;

        const recurrenceObject = values.rules ? { ...values.rules, ...defaultRecurringDates } : defaultRecurringDates;

        const event = {
          id: eventToEdit ? eventToEdit.id : null,
          subject: values.subject,
          start: !isAllDayEvent ? startEventDate : startEventDate.clone().utc(true).startOf('day'),
          end: !isAllDayEvent ? endEventDate : endEventDate.clone().utc(true).endOf('day'),
          type: values.type,
          calendarCategoryId: values.calendarCategoryId,
          reminderToCustomEmail: values.reminderToCustomEmail,
          isCheckedReminder: values.isCheckedReminder,
          agencyEMail: values.agencyEMail,
          agentEMail: values.agentEMail,
          csrEMail: values.csrEMail,
          noneSending: values.noneSending,
          recurrenceObject: recurrenceObject,
          recurrenceParentId: eventToEdit ? eventToEdit.recurrenceParentId : null,
        };

        handleModalOk(event).then(() => {
          clearForm();
        });
      })
      .catch((errorInfo) => {});
  };

  const handleCancel = () => {
    clearForm();

    handleModalCancel();
  };

  const clearForm = () => {
    form.resetFields();

    setSelectedFrequency(recurrence.FREQUENCY_TYPES.Daily);
    setEnds(recurrence.RECURRENCE_ENDS.NEVER);
  };

  const initRules = (eventObj) => {
    let obj = !eventObj || !eventObj.recurrenceObject ? null : eventObj.recurrenceObject;

    if (!obj) {
      const days = !eventObj || !eventObj.startObj.isSameOrAfter(eventObj.endObj, 'day') ? [0, 1, 2, 3, 4, 5, 6] : [eventObj.startObj.day()];
      return {
        interval: 1,
        byDay: days,
        ends: ends,
        frequency: selectedFrequency,
      };
    }

    let momentUntil;
    if (!!obj.until) {
      momentUntil = moment(obj.until).local();
      obj = { ...obj, ...{ until: momentUntil } };
    }

    if (obj.count && obj.count > 0) {
      obj = { ...obj, ...{ ends: recurrence.RECURRENCE_ENDS.AFTER } };
      setEnds(recurrence.RECURRENCE_ENDS.AFTER);
    } else if (momentUntil) {
      obj = { ...obj, ...{ ends: recurrence.RECURRENCE_ENDS.ON } };
      setEnds(recurrence.RECURRENCE_ENDS.ON);
    } else {
      obj = { ...obj, ...{ ends: recurrence.RECURRENCE_ENDS.NEVER } };
      setEnds(recurrence.RECURRENCE_ENDS.NEVER);
    }

    if (!!obj.frequency) {
      setSelectedFrequency(obj.frequency);
    }

    return obj;
  };

  const getFormFieldValue = (fieldName) => {
    return Form.useWatch(fieldName, form);
  };

  const onChangeFrequency = (val) => {
    setSelectedFrequency(val);
    form.setFieldValue(['rules', 'choice'], null);
  };

  const onChangeAllDay = (val) => {
    if (val) {
      setShowDurationTime(null);
      setDurationFormat('YYYY-MM-DD');
    } else {
      setShowDurationTime('HH:mm');
      setDurationFormat('YYYY-MM-DD HH:mm');
    }
  };

  return (
    <Modal
      title={title}
      getContainer={rootContainer}
      open={isOpen}
      width={500}
      onOk={handleOk}
      confirmLoading={saveEventLoading}
      onCancel={handleCancel}
      destroyOnClose={true}
      maskClosable={false}
      closable={false}
    >
      <Form
        form={form}
        name="event-form"
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17 }}
        scrollToFirstError={true}
        requiredMark={false}
        autoComplete="off"
        size={'small'}
      >
        <Form.Item label="Subject" name="subject" rules={[{ required: true, message: 'Required' }]}>
          <Input.TextArea />
        </Form.Item>

        <Form.Item label="Duration" name="duration" rules={[{ required: true, message: 'Required' }]}>
          <RangePicker showTime={{ format: showDurationTime }} format={durationFormat} getPopupContainer={rootContainer} />
        </Form.Item>

        {/* <Form.Item label="All day" name="allDay" valuePropName="checked">
          <Switch onChange={onChangeAllDay} />
        </Form.Item> */}

        <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Required' }]}>
          <Select allowClear getPopupContainer={rootContainer}>
            <Option value="0">My Task</Option>
            <Option value="1">Agency Task</Option>
          </Select>
        </Form.Item>

        <Form.Item name="calendarCategoryId" label="Category">
          <Select optionLabelProp="children" allowClear getPopupContainer={rootContainer}>
            {calendarCategories && calendarCategories.length
              ? calendarCategories.map((x) => (
                  <Option key={x.id} value={x.id}>
                    <Badge color={x.color} /> {x.categoryName}
                  </Option>
                ))
              : null}
          </Select>
        </Form.Item>

        <Form.Item
          label="Custom Email"
          name="reminderToCustomEmail"
          rules={[
            {
              pattern: /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/,
              message: 'Invalid email format',
            },
          ]}
          tooltip={{
            title: `If specified reminder will be sent to email and event will be shown under Activity Reminders for user if their email matches specified.`,
            icon: <InfoCircleOutlined />,
            getPopupContainer: rootContainer,
          }}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Reminder" name="isCheckedReminder" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          noStyle
          wrapperCol={{ offset: 7, span: 17 }}
          shouldUpdate={(prevValues, curValues) => prevValues.isCheckedReminder !== curValues.isCheckedReminder}
        >
          {({ getFieldValue }) => {
            return getFieldValue('isCheckedReminder') === true ? (
              <Row>
                <Col offset={7} span={7}>
                  <Form.Item
                    noStyle
                    name="agencyEMail"
                    valuePropName="checked"
                    tooltip={{ title: 'Tooltip with customize icon', icon: <InfoCircleOutlined />, getPopupContainer: rootContainer }}
                  >
                    <Checkbox>Agency Emails</Checkbox>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    noStyle
                    name="agentEMail"
                    valuePropName="checked"
                    tooltip={{ title: 'Tooltip with customize icon', icon: <InfoCircleOutlined />, getPopupContainer: rootContainer }}
                  >
                    <Checkbox>Agents Emails</Checkbox>
                  </Form.Item>
                </Col>

                <Col offset={7} span={7}>
                  <Form.Item
                    noStyle
                    name="csrEMail"
                    valuePropName="checked"
                    tooltip={{ title: 'Tooltip with customize icon', icon: <InfoCircleOutlined />, getPopupContainer: rootContainer }}
                  >
                    <Checkbox>CSR Agents</Checkbox>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    noStyle
                    name="noneSending"
                    valuePropName="checked"
                    tooltip={{ title: 'Tooltip with customize icon', icon: <InfoCircleOutlined />, getPopupContainer: rootContainer }}
                  >
                    <Checkbox>None</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            ) : null;
          }}
        </Form.Item>

        <Form.Item label="Recurrent" name="isCheckedRecurrent" valuePropName="checked">
          <Switch />
        </Form.Item>

        {getFormFieldValue('isCheckedRecurrent') === true && (
          <Card>
            <Form.Item
              noStyle
              wrapperCol={{ offset: 0 }}
              shouldUpdate={(prevValues, curValues) => prevValues.isCheckedRecurrent !== curValues.isCheckedRecurrent}
            >
              <FrequencyItem onChange={onChangeFrequency} selectedValue={selectedFrequency} />

              <FrequencyContainer form={form} selectedFrequency={selectedFrequency} rootContainer={rootContainer} />

              <Divider type="horizontal" />

              <EndsContainer form={form} selectedEnds={ends} rootContainer={rootContainer} />
            </Form.Item>
          </Card>
        )}
      </Form>
    </Modal>
  );
};

export default EditEventModal;
